/**
 * 聊天头部组件
 */

import React from 'react';
import { useServerStore, useThemeStore } from '@/stores';
import type { ThemePreference } from '@/stores';
import { useIsLightUi } from '@/hooks';
import { headerInline, themeSegInline } from '@/utils/themeInline';
import { ServerSelector } from './ServerSelector';

interface ChatHeaderProps {
  wsState: 'connected' | 'connecting' | 'disconnected' | 'error';
  onClose: () => void;
  onClear: () => void;
  onSwitchServer: (serverId: number, name: string, ip: string) => void;
}

const THEME_SEGMENTS: { pref: ThemePreference; label: string; title: string }[] = [
  { pref: 'system', label: '自动', title: '跟随系统浅色/深色' },
  { pref: 'light', label: '浅色', title: '始终浅色' },
  { pref: 'dark', label: '深色', title: '始终深色' },
];

export const ChatHeader: React.FC<ChatHeaderProps> = ({ wsState, onClose, onClear, onSwitchServer }) => {
  const { toggleFullscreen, activeServerId, activeServerName, activeServerIp } = useServerStore();
  const themePreference = useThemeStore((s) => s.themePreference);
  const setThemePreference = useThemeStore((s) => s.setThemePreference);
  const isLightUi = useIsLightUi();
  
  // WebSocket：绿点已连、黄点连接中、红点断开/异常（悬停看说明）
  const wsStatusConfig = {
    connected: { label: 'WebSocket 已连接', className: 'ws-connected' },
    connecting: { label: '正在连接 WebSocket', className: 'ws-connecting' },
    disconnected: { label: 'WebSocket 未连接', className: 'ws-disconnected' },
    error: { label: 'WebSocket 连接异常', className: 'ws-error' },
  };

  const wsStatus = wsStatusConfig[wsState] || wsStatusConfig.disconnected;
  
  return (
    <div className="sse-ai-header" style={headerInline(isLightUi)}>
      <div className="sse-ai-header-left">
        <div className="ai-icon">🤖</div>
        <div className="ai-title">AI 运维助手</div>
        {activeServerId && (
          <ServerSelector
            currentServerId={activeServerId}
            currentServerName={activeServerName}
            currentServerIp={activeServerIp}
            onSwitch={onSwitchServer}
          />
        )}
      </div>
      <div className="sse-ai-header-actions">
        <div
          className="sse-ai-theme-seg"
          role="group"
          aria-label="外观主题"
          style={themeSegInline(isLightUi)}
        >
          {THEME_SEGMENTS.map(({ pref, label, title }) => (
            <button
              key={pref}
              type="button"
              className={`sse-ai-theme-seg-btn${themePreference === pref ? ' is-active' : ''}`}
              title={title}
              aria-pressed={themePreference === pref}
              onClick={() => setThemePreference(pref)}
            >
              {label}
            </button>
          ))}
        </div>
        <span
          className={`ws-status-dot ${wsStatus.className}`}
          role="status"
          aria-label={wsStatus.label}
          title={wsStatus.label}
        />
        <button 
          className="sse-ai-header-btn sse-ai-fullscreen" 
          title="全屏"
          onClick={toggleFullscreen}
        >
          <span className="dashicons dashicons-fullscreen-alt"></span>
        </button>
        <button 
          className="sse-ai-header-btn sse-ai-clear" 
          title="清空"
          onClick={onClear}
        >
          <span className="dashicons dashicons-trash"></span>
        </button>
        <button 
          className="sse-ai-header-btn sse-ai-close" 
          title="关闭"
          onClick={onClose}
        >
          <span className="dashicons dashicons-no-alt"></span>
        </button>
      </div>
    </div>
  );
};